import { useInfiniteQuery } from '@tanstack/react-query';

import { Groups } from '@app/client';
// TODO: This should instead use `NftFilterPodQuery`.
import { type GroupItemsFilterQuery } from '@app/client/common/structs/groups/groups';

import { getNextPageParam, getPagesContent } from './utils';

export type FilteredItemsReturn = ReturnType<typeof useFilteredItems>;
export type FilteredItemsDataResponse = FilteredItemsReturn['items'];
export type FilteredItemsSingleItem = FilteredItemsDataResponse[number];

// TODO: We could consider having the `async callback` be passed in.
// We might have trouble getting the types right, but it would allow
// us to re-use this same hook between Pods and Profile.
export interface FilteredItemsOptions {
    groupId?: string;
    filters?: GroupItemsFilterQuery;
    // TODO: Consider `limit` (items per-page) an option.
}

// TODO: This would normally be located in `groupsCacheKeys`,
// but since this file is a fork, I am implementing my own solution.
export function getQueryKey(groupId?: string, filters?: GroupItemsFilterQuery) {
    return ['group', groupId, 'items', filters ?? 'unfiltered'];
}

export function useFilteredItems({ groupId, filters }: FilteredItemsOptions) {
    // TODO: Do we really need `isPreviousData`? Is that an indicator for "stale results"?
    // TODO: Should we rename any of these props? Ex: `hasMoreResults`, `loadMoreResults`.

    const {
        // Extracting only a subset of query props.
        data,
        error,
        isError,
        isLoading,
        isSuccess,
        isFetching,
        isPreviousData,
        hasNextPage,
        fetchNextPage,
        refetch,
    } = useInfiniteQuery(
        getQueryKey(groupId, filters),
        async ({ pageParam = 0, signal }) => {
            return Groups.callGetGroupItems({
                groupId,
                query: {
                    page: pageParam || 0,
                    ...filters,
                },
                settings: {
                    signal,
                },
            });
        },
        {
            keepPreviousData: true,
            retry: false,
            enabled: Boolean(groupId),
            getNextPageParam,
        },
    );

    let collections: string[] = [];
    if (data?.pages) {
        const content = getPagesContent(data?.pages);
        collections = content?.length
            ? Array.from([...new Set(content.flatMap((result) => result.item.item_type.Nft.slug || []))])
            : [];
    }

    return {
        data,
        items: data?.pages?.flatMap((page) => page?.data?.map((item) => item)) ?? [],
        total: data?.pages?.[0].paging.total || 0,
        noDataResponse: data === undefined,
        error,
        isError,
        isLoading,
        isSuccess,
        isFetching,
        isPreviousData,
        hasNextPage,
        fetchNextPage,
        refetch,
        collections,
    };
}
